import z from "zod";
import { type EOUProvider, eouProviders } from "@/models/eou";
import { type LLMProvider, llmProviders } from "@/models/llm";
import { type STTProvider, sttProviders } from "@/models/stt";
import { type TTSProvider, ttsProviders } from "@/models/tts";
import { type VADProvider, vadProviders } from "@/models/vad";
import { PluginServer } from "@/plugins/server/class";
import type { PluginDefinition } from "@/plugins/server/types";
import type { SerializableValue } from "@/shared/canon";
import { lifeError } from "@/shared/error";
import * as op from "@/shared/operation";
import type { Todo } from "@/shared/types";
import type { TelemetryClient } from "@/telemetry/clients/base";
import { createTelemetryClient } from "@/telemetry/clients/node";
import { TransportNodeClient } from "@/transport/client/node";
import type { agentServerConfig } from "./config";
import type { AgentDefinition, AgentScope } from "./types";

export class AgentServer {
  readonly def: AgentDefinition;
  readonly id: string;
  readonly sha: string;
  readonly config: z.output<typeof agentServerConfig.schema>;
  readonly transport: TransportNodeClient;
  readonly storage = null;
  readonly models: {
    vad: InstanceType<VADProvider>;
    stt: InstanceType<STTProvider>;
    eou: InstanceType<EOUProvider>;
    llm: InstanceType<LLMProvider>;
    tts: InstanceType<TTSProvider>;
  };
  readonly plugins: Record<string, PluginServer<PluginDefinition>> = {};
  readonly scope: AgentScope<AgentDefinition["scope"]>;
  readonly isRestart: boolean;
  readonly telemetry: TelemetryClient;
  readonly #initialPluginsContexts: Record<string, SerializableValue>;

  constructor({
    id,
    sha,
    definition,
    scope,
    config,
    pluginsContexts,
    isRestart,
  }: {
    id: string;
    sha: string;
    definition: AgentDefinition;
    config: z.output<typeof agentServerConfig.schema>;
    scope?: AgentScope<AgentDefinition["scope"]>;
    isRestart?: boolean;
    pluginsContexts?: Record<string, SerializableValue>;
  }) {
    this.id = id;
    this.sha = sha;
    this.def = definition;
    this.scope = scope ?? this.def.scope.schema.parse({});
    this.config = config;
    this.isRestart = isRestart ?? false;
    this.#initialPluginsContexts = pluginsContexts ?? {};

    // Initialize telemetry
    this.telemetry = createTelemetryClient("agent.server", {
      agentId: id,
      agentSha: this.sha,
      agentName: this.def.name,
      agentConfig: this.config,
      transportProviderName: this.config.transport.provider,
      llmProviderName: this.config.models.llm.provider,
      sttProviderName: this.config.models.stt.provider,
      eouProviderName: this.config.models.eou.provider,
      ttsProviderName: this.config.models.tts.provider,
      vadProviderName: this.config.models.vad.provider,
    });

    // Initialize transport
    this.transport = new TransportNodeClient({
      config: this.config.transport,
      obfuscateErrors: true,
      telemetry: this.telemetry,
    });

    // Initialize storage
    // TODO

    // Initialize models
    const vadProvider = vadProviders[this.config.models.vad.provider];
    const sttProvider = sttProviders[this.config.models.stt.provider];
    const eouProvider = eouProviders[this.config.models.eou.provider];
    const llmProvider = llmProviders[this.config.models.llm.provider];
    const ttsProvider = ttsProviders[this.config.models.tts.provider];
    this.models = {
      vad: new vadProvider.class(this.config.models.vad),
      stt: new sttProvider.class(this.config.models.stt),
      eou: new eouProvider.class(this.config.models.eou as Todo),
      llm: new llmProvider.class(this.config.models.llm as Todo),
      tts: new ttsProvider.class(this.config.models.tts),
    };

    // Validate plugins
    this.#initializePlugins();
  }

  #initializePlugins() {
    // Validate plugins have unique names
    const pluginNames = this.def.plugins.map((p) => p.name);
    const duplicates = pluginNames.filter((name, index) => pluginNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      const uniqueDuplicates = [...new Set(duplicates)];
      throw lifeError({
        code: "Validation",
        message: `Two or more plugins are named "${uniqueDuplicates.join('", "')}". Plugin names must be unique. (agent: '${this.def.name}')`,
      });
    }

    // Validate plugin dependencies
    for (const plugin of this.def.plugins) {
      for (const dependency of plugin.dependencies) {
        // - Ensure the plugin is provided
        const depPlugin = this.def.plugins.find((p) => p.name === dependency.name);
        if (!depPlugin) {
          throw lifeError({
            code: "Validation",
            message: `Plugin '${plugin.name}' depends on plugin '${dependency.name}', but '${dependency.name}' is not registered. (agent: '${this.def.name}')`,
          });
        }
      }
    }

    // Expose 'has-plugin-server' via RPC
    this.transport.register({
      name: "agent.has-plugin-server",
      schema: {
        input: z.object({ pluginName: z.string() }),
        output: z.object({ hasServer: z.boolean() }),
      },
      execute: ({ pluginName }) => {
        const plugin = this.plugins?.[pluginName];
        if (!plugin) return op.success({ hasServer: false });
        return op.success({ hasServer: true });
      },
    });
  }

  async start() {
    return await this.telemetry.trace("AgentServer.start()", async () => {
      try {
        // Create plugin servers
        for (const definition of this.def.plugins) {
          // Parse the initial context
          const { error: errorContext, data: parsedContext } = definition.context.schema.safeParse(
            this.#initialPluginsContexts?.[definition.name] ?? {},
          );
          if (errorContext) {
            return op.failure({
              code: "Validation",
              message: `Invalid initial context value for plugin '${definition.name}'.`,
              cause: errorContext,
            });
          }

          // Parse the config
          const { error: errorConfig, data: parsedConfig } = definition.config.schema.safeParse(
            this.def.pluginConfigs?.[definition.name] ?? {},
          );
          if (errorConfig) {
            return op.failure({
              code: "Validation",
              message: `Invalid config value for plugin '${definition.name}'.`,
              cause: errorConfig,
            });
          }

          const [errCreate, instance] = op.attempt(
            () =>
              new PluginServer({
                agent: this,
                definition,
                config: parsedConfig,
                context: parsedContext as SerializableValue,
              }),
          );
          if (errCreate) return op.failure(errCreate);
          this.plugins[definition.name] = instance;
        }

        // Start all plugin servers
        const result = await Promise.all(Object.values(this.plugins).map((p) => p.start()));
        const err = result.find((r) => r[0])?.[0];
        if (err) return op.failure(err);

        // Return that the agent server was started successfully
        return op.success();
      } catch (error) {
        return op.failure({ code: "Unknown", cause: error });
      }
    });
  }

  async stop() {
    return await this.telemetry.trace("AgentServer.stop()", async (span) => {
      try {
        // Stop all plugins
        await Promise.all(
          Object.entries(this.plugins).map(([pluginId, plugin]) =>
            plugin
              .stop()
              .catch((error) =>
                span.log.error({ message: `Error stopping plugin ${pluginId}:`, error }),
              ),
          ),
        );

        // Disconnect transport
        const [errLeave] = await this.transport.leaveRoom();
        if (errLeave) return op.failure(errLeave);

        // Return that the agent server was stopped successfully
        return op.success();
      } catch (error) {
        return op.failure({ code: "Unknown", cause: error });
      }
    });
  }
}

// notify: (
//   { emit, context },
//   params: {
//     source: "user" | "application";
//     behavior: "inform" | "interrupt" | "decide";
//     message: string;
//   },
// ) => {
//   // Insert the notification message
//   const message = `${params.source === "user" ? "User" : "Application"} update: ${params.message}`;
//   emit({
//     type: "operation.message",
//     data: {
//       id: generateId(),
//       role: params.source === "user" ? "user" : "system",
//       message,
//     },
//   });

//   // If the behavior is "discrete", return
//   if (params.behavior === "inform") return;
//   // Else, if the behavior is interrupt, run continue
//   else if (params.behavior === "interrupt")
//     emit({
//       type: "operation.continue",
//       data: {
//         messages: context.messages,
//         insertPolicy: "abrupt-interrupt",
//         allowInterruption: true,
//       },
//     });
//   // Else, if the behavior is decide, decide whether to make the notification interrupt or not
//   else if (params.behavior === "decide") {
//     emit({
//       type: "operation.decide",
//       data: { messages: [], insertPolicy: "abrupt-interrupt", allowInterruption: true },
//     });
//   }
// },
// ask: ({ emit, context }, message: string) => {
//   emit({
//     type: "operation.message",
//     data: { id: generateId(), role: "user", message: message },
//   });
//   emit({ type: "operation.continue", data: { messages: context.messages } });
// },
// prompt: ({ emit, context }, message: string) => {
//   emit({
//     type: "operation.message",
//     data: { id: generateId(), role: "system", message: message },
//   });
//   emit({ type: "operation.continue", data: { messages: context.messages } });
// },
